import { cloneDeep } from "lodash";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import ActionButtons from "../../../components/Calculator/ActionButtons";
import VesselSelection from "../../../components/Calculator/VesselSelection";
import { PieChart } from "../../../components/Charts/PieChart";
import {
  initialAddFleet,
  termsCoEfficientTable
} from "../../../constants";
import { toastError } from "../../../utils";
import * as actions from "../Calculator/actions";
import {
  addPortDistanceToOperations,
  addPortDistanceToSelectedFleet,
  calculationValidation,
  convertPortOperationToDistanceString,
  fleetWithCalculatorDataMapping,
} from "../Calculator/dataMapping";

/**
 * Builds the initial calculatorData for ONE copy.
 */
function buildInitialCalculatorData(analysis, index) {
  const vessel = analysis?.vessel || {};
  const clientQuery = analysis?.clientQuery || {};
  const cargoList = clientQuery?.cargoList || [];

  let formattedPortOperations = cargoList.map((portElement, i) => ({
    ...portElement,
    index: i,
  }));

  let processedQuery = { ...clientQuery };
  if (!processedQuery.fuelCalculationMethod)
    processedQuery.fuelCalculationMethod = "DEFAULT";
  if (!processedQuery.fuelType) processedQuery.fuelType = "TYPE0";

  if (processedQuery.fuelCalculationMethod === "DEFAULT") {
    formattedPortOperations = formattedPortOperations.map((item) => ({
      ...item,
      cargoDetails: {
        ...item?.cargoDetails,
        fuelPriceType0Hsfo: 0,
        fuelPriceType0Lsfo: 0,
        fuelPriceType0Mgo: 0,
        fuelPriceType1Hsfo: 0,
        fuelPriceType1Lsfo: 0,
        fuelPriceType1Mgo: 0,
        fuelPriceType2Hsfo: 0,
        fuelPriceType2Lsfo: 0,
        fuelPriceType2Mgo: 0,
        fuelPriceType3Hsfo: 0,
        fuelPriceType3Lsfo: 0,
        fuelPriceType3Mgo: 0,
        fuelPriceType4Hsfo: 0,
        fuelPriceType4Lsfo: 0,
        fuelPriceType4Mgo: 0,
      },
    }));
  }

  const vesselCopy = {
    ...vessel,
    _id: `${vessel._id || uuidv4()}-copy-${index}`,
  };

  return {
    portOperation: formattedPortOperations,
    clientQuery: processedQuery,
    fleetList: [vesselCopy],
    cargoList: [],
    cargoExtraDetails: [],
    autoSave: false,
  };
}

function SingleAnalysisCopy({ analysis, copyIndex, setLoader, loader = false }) {
  const [copies, setCopies] = useState(() =>
    Array(10)
      .fill(null)
      .map((_, i) => buildInitialCalculatorData(analysis, i)),
  );

  const [selectedIndexState, setSelectedIndexState] = useState(0);
  const selectedIndexRef = useRef(0);

  const setSelectedIndex = (index) => {
    selectedIndexRef.current = index;
    setSelectedIndexState(index);
  };

  const selectedIndex = selectedIndexState;

  const [selectedVessels, setSelectedVessels] = useState(
    copies[0].fleetList[0],
  );
  const [syncBunker, setSyncBunker] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const [hideOrder, setHideOrder] = useState(true);
  const [hideCalculator, setHideCalculator] = useState(false);
  const [addFleetModal, setAddFleetModal] = useState(false);
  const [addFleetsArray, setAddFleetsArray] = useState([initialAddFleet]);
  const [splitHireModal, setSplitHireModal] = useState(false);
  const [selectedSplitHire, setSelectedSplitHire] = useState({});
  const [voyagesDurationData, setVoyagesDurationData] = useState([]);
  const [voyagesExpensesBreakupData, setVoyagesExpensesBreakupData] = useState([]);
  const [fleetCheckbox, setFleetCheckbox] = useState({
    ballest: { 1: true, 2: true, 3: true, 4: true },
    laden: { 1: true, 2: true, 3: true, 4: true },
  });
  const [marginModal, setMarginModal] = useState(false);
  const [supplyModal, setSupplyModal] = useState(false);
  const [openAddOperation, setOpenAddOperation] = useState(false);
  const [openAddOperationindex, setOpenAddOperationindex] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const fetchPortDistance = useRef(true);
  const [portDistanceResponse, setPortDistanceResponse] = useState({});

  const analysisId = analysis?._id || "";

  const proxyCalculatorData = useMemo(() => {
    const activeCopy = copies[selectedIndex];

    const combinedFleetList = copies.map((copy) => copy.fleetList[0]);

    return {
      ...activeCopy,
      fleetList: combinedFleetList,
    };
  }, [copies, selectedIndex]);

  const setProxyCalculatorData = useCallback((newDataOrFunc) => {
    setCopies((prevCopies) => {
      const newCopies = [...prevCopies];
      const targetIndex = selectedIndexRef.current;

      const activeCopy = newCopies[targetIndex];
      const combinedFleetList = newCopies.map((copy) => copy.fleetList[0]);

      const currentProxyData = {
        ...activeCopy,
        fleetList: combinedFleetList,
      };

      const newData =
        typeof newDataOrFunc === "function"
          ? newDataOrFunc(currentProxyData)
          : newDataOrFunc;

      newCopies[targetIndex] = {
        ...newCopies[targetIndex],
        clientQuery: newData.clientQuery || newCopies[targetIndex].clientQuery,
        portOperation:
          newData.portOperation || newCopies[targetIndex].portOperation,
        cargoExtraDetails:
          newData.cargoExtraDetails || newCopies[targetIndex].cargoExtraDetails,
        cargoList: newData.cargoList || newCopies[targetIndex].cargoList,
      };

      if (newData.fleetList && newData.fleetList.length > 0) {
        newData.fleetList.forEach((fleetItem, index) => {
          if (newCopies[index]) {
            newCopies[index] = {
              ...newCopies[index],
              fleetList: [fleetItem],
            };
          }
        });
      }
      return newCopies;
    });
  }, []);

  const calculationErrors = useMemo(() => {
    if (
      selectedVessels &&
      proxyCalculatorData?.portOperation?.length &&
      proxyCalculatorData?.fleetList?.length
    ) {
      return calculationValidation(proxyCalculatorData, selectedVessels);
    }
    return [];
  }, [proxyCalculatorData, selectedVessels]);

  const sendCalculatorResponse = (modifiedData = {}) => {
    return new Promise((resolve, reject) => {
      if (modifiedData.fleetList.length === 0) return resolve({});

      const updatedCargoList = modifiedData?.cargoList?.map((cargoDetails) => {
        const { _id, ...restCargoMainDetails } = cargoDetails;
        const { _id: cargoDetailId, ...restCargoDetail } =
          cargoDetails?.cargoDetails || {};
        return {
          ...restCargoMainDetails,
          cargoDetails: { ...restCargoDetail },
        };
      });

      const {
        _id,
        cargoList,
        createdUser,
        lastModifiedUser,
        userId,
        calculationFormat,
        __v,
        createdAt,
        updatedAt,
        isCalculate,
        ...restClientQuery
      } = modifiedData.clientQuery;
      const updatedClientQuery = { ...restClientQuery, cargoList: null };

      const updatedFleetListData =
        modifiedData?.fleetList?.length > 0
          ? modifiedData.fleetList.map((fleetDetails, index) => {
            const vesselDetails = !!fleetDetails?.vessel
              ? JSON.parse(fleetDetails.vessel)
              : {};
            const {
              _id: parseVesselId,
              userId: parseVesselUserId,
              createdUser: parseVesselCreatedUser,
              lastModifiedUser: parseVesselModifiedUser,
              vesselExtraDetails,
              __v: parseVessel__v,
              createdAt,
              createdDate,
              lastModifiedDate,
              pattern,
              updatedAt,
              vesselLabel,
              vesselAHL,
              vesselCO2,
              vesselCountry,
              vesselGrabs,
              vesselIceClass,
              vesselIMO,
              vesselLdnConsmMGO1,
              vesselLdnConsmMGO2,
              vesselLightShip,
              vesselMMSI,
              vesselType,
              vesselSource,
              vesselName,
              ...parseVesselDetails
            } = vesselDetails;
            const {
              _id,
              vesselId,
              createdUser,
              organizationId,
              userId,
              lastModifiedUser,
              vesselUniqueId,
              __v,
              ...rest
            } = fleetDetails;
            return {
              ...rest,
              vessel: JSON.stringify({
                id: vesselUniqueId,
                ...parseVesselDetails,
                system: true,
              }),
              vesselId: vesselUniqueId,
            };
          })
          : [];

      const updatedCargoExtraDetails = modifiedData?.cargoExtraDetails?.map(
        (cargoextraDetails) => {
          const { _id, ...restCargoMainDetails } = cargoextraDetails;
          const { _id: cargoDetailId, ...restCargoDetail } =
            cargoextraDetails?.cargoDetails || {};
          return {
            ...restCargoMainDetails,
            cargoDetails: { ...restCargoDetail },
          };
        },
      );

      const calculationData = {
        autoSave: false,
        cargoExtraDetails: updatedCargoExtraDetails,
        cargoList: updatedCargoList,
        clientQuery: updatedClientQuery,
        fleetList: updatedFleetListData,
      };

      actions
        .sendCalculatorData(calculationData)
        .then((res) => resolve(res))
        .catch((err) => {
          resolve({});
          toastError(err?.data?.message || "Something went wrong");
        });
    });
  };

  const onFetchAndSetInitialPortDistance = (propCalculator) => {
    if (!fetchPortDistance.current) return Promise.resolve({});
    let updatedCalculatorData = { ...propCalculator };
    const { getPortDistanceString, routeList } =
      convertPortOperationToDistanceString(updatedCalculatorData);
    const { clientQuery = {} } = updatedCalculatorData;
    const {
      eca = false,
      hra = false,
      jwc = false,
      suez = false,
      panama = false,
      singapore = false,
    } = clientQuery;

    const portDataObj = {
      eca,
      hra,
      jwc,
      suez,
      panama,
      singapore,
      routeOverride: false,
      getGeometry: false,
      routeList,
      portList: getPortDistanceString,
      clientQueryId: updatedCalculatorData.clientQuery._id,
      queryId: updatedCalculatorData.clientQuery._id,
      id: analysisId,
    };

    setLoader(true, "Calculating port distances...");
    return new Promise((resolve) => {
      actions
        .getPortDistanceByPortList(portDataObj)
        .then((res) => {
          setLoader(false);
          fetchPortDistance.current = false;
          setPortDistanceResponse(res.data);
          resolve(res?.data);
        })
        .catch(() => {
          setLoader(false);
          resolve({});
        });
    });
  };

  const sendCalculationRequest = async (sendData) => {
    let portDistanceRes = sendData?.portDistanceResponse || portDistanceResponse;
    if (fetchPortDistance.current) {
      portDistanceRes = await onFetchAndSetInitialPortDistance(sendData);
    }
    sendData = {
      ...sendData,
      portOperation: addPortDistanceToOperations(
        portDistanceRes,
        [...sendData.portOperation],
        sendData?.realData || {},
        false,
        sendData?.reset || false
      ),
    };

    const activeFleet = sendData.fleetList[selectedIndex];
    const updatedActiveFleet = { ...activeFleet, vesselUniqueId: uuidv4() };

    const modifiedData = {
      cargoList: sendData.portOperation.filter((item) => !item.isExtra),
      cargoExtraDetails: sendData.portOperation.filter(
        (item) => !!item.isExtra,
      ),
      fleetList: [updatedActiveFleet],
      clientQuery: {
        ...sendData.clientQuery,
        calculationFormat: sendData.portOperation
          .map((item) => item._id)
          .join(","),
      },
      autoSave: false,
    };

    setLoader(true, `Calculating...`);
    const res = await sendCalculatorResponse(modifiedData);
    setLoader(false);

    const resultFleetDataArray = fleetWithCalculatorDataMapping(
      [cloneDeep(updatedActiveFleet)],
      res?.data,
    );
    const calculatedActiveFleet = resultFleetDataArray[0] || {};

    const newCombinedFleetList = [...sendData.fleetList];
    newCombinedFleetList[selectedIndex] = calculatedActiveFleet;

    onChangeFleetRowSelect(
      calculatedActiveFleet,
      { ...sendData, fleetList: newCombinedFleetList },
      true,
      true,
      true,
    );
  };

  const onChangeFleetRowSelect = async (
    fleet = {},
    resultCalculatorData = null,
    changeVessel = false,
    isPortDistanceChanged = false,
    isFromCalculateButton = false,
  ) => {
    let targetIndex = selectedIndex;
    if (fleet._id) {
      const match = fleet._id.match(/-copy-(\d+)$/);
      if (match) targetIndex = parseInt(match[1], 10);
    }

    if (targetIndex !== selectedIndex) {
      setSelectedIndex(targetIndex);
    }

    let baseCalculatorData = resultCalculatorData
      ? resultCalculatorData
      : proxyCalculatorData;

    const targetQuery = baseCalculatorData?.clientQuery || copies[targetIndex].clientQuery;
    let portOperation = [...(baseCalculatorData?.portOperation || copies[targetIndex].portOperation)];

    let commencePort = (fleet && fleet.port) || "";
    let fleetFuelType =
      (fleet && fleet.vessel && JSON.parse(fleet.vessel)) || {};
    fleetFuelType = fleetFuelType.fuelType || "TYPE0";

    const portToPortOperationResultMap =
      (fleet && fleet.portToPortOperationResultMap) || [];
    portOperation = portOperation.map((operation, index) => {
      const selectedResultMap = portToPortOperationResultMap[index] || {};
      const removeDistanceFromResultMap = ({
        distanceECA = "",
        distanceNormal = "",
        ...rest
      }) => rest;
      if (index !== 0) commencePort = (operation && operation.port) || "";
      let isTermExists = termsCoEfficientTable[0];
      if (operation.terms) {
        isTermExists = termsCoEfficientTable.find(
          (item) => item.label === operation.terms,
        );
      }
      let cargoDetails = { ...operation.cargoDetails };
      if (index === 0) {
        const currentType = fleetFuelType.replace("TYPE", "");
        const {
          bnkDelhs = 0,
          bnkDells = 0,
          bnkDelmgo = 0,
          bnkPriceDeliveryhs = 0,
          bnkPriceDeliveryls = 0,
          bnkPriceDeliverymgo = 0,
        } = fleet;
        cargoDetails = {
          ...cargoDetails,
          bunkerSuppliedHSFO: bnkDelhs,
          bunkerSuppliedLSFO: bnkDells,
          bunkerSuppliedMGO: bnkDelmgo,
          [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
          [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
          [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
        };
      }
      return {
        ...operation,
        ...removeDistanceFromResultMap(selectedResultMap),
        cargoDetails,
        port: commencePort,
        terms: isTermExists.label,
        factor: isTermExists.value,
      };
    });

    if (!isPortDistanceChanged) {
      if (fetchPortDistance.current) {
        let portDistanceRes = await onFetchAndSetInitialPortDistance({
          ...baseCalculatorData,
          clientQuery: targetQuery,
        });
        portOperation = addPortDistanceToOperations(portDistanceRes, [
          ...portOperation,
        ]);

        setCopies((prevCopies) =>
          prevCopies.map((copy, idx) => {
            if (idx === targetIndex) return copy;
            return {
              ...copy,
              portOperation: addPortDistanceToOperations(portDistanceRes, [
                ...copy.portOperation,
              ]),
            };
          }),
        );
      }
    }

    fleet = addPortDistanceToSelectedFleet(portOperation, fleet);

    if (changeVessel || !selectedVessels._id) {
      setSelectedVessels(fleet);
    }

    const newFleetList = baseCalculatorData.fleetList.map((sf) =>
      sf._id === fleet._id ? fleet : sf,
    );

    setCopies((prevCopies) => {
      const nextCopies = [...prevCopies];

      nextCopies[targetIndex] = {
        ...nextCopies[targetIndex],
        clientQuery: { ...targetQuery, fuelType: fleetFuelType },
        portOperation,
      };

      newFleetList.forEach((fleetItem, index) => {
        if (nextCopies[index]) {
          nextCopies[index] = {
            ...nextCopies[index],
            fleetList: [fleetItem],
          };
        }
      });

      return nextCopies;
    });
  };

  const onChangePortOperations = (index, e, cargoUpdate = false) => {
    let { value = "", name = "" } = e.target;
    let portOperation = [...proxyCalculatorData.portOperation];
    if (name === "terms") {
      const termFactor = termsCoEfficientTable.find(
        (item) => item.label === value,
      );
      portOperation[index]["terms"] = value;
      portOperation[index]["factor"] = (termFactor && termFactor.value) || 0;
    } else if (cargoUpdate) {
      if (!portOperation[index].cargoDetails)
        portOperation[index].cargoDetails = {};
      portOperation[index].cargoDetails[name] = value;
    } else {
      portOperation[index][name] = value;
    }
    setProxyCalculatorData({ ...proxyCalculatorData, portOperation });
  };

  const onAddOperations = () => {
    toastError("Modifying ports is disabled in this view.");
  };
  const onDeleteOperations = () => {
    toastError("Modifying ports is disabled in this view.");
  };
  const onFetchPortOperationDistance = (
    reset = false,
    propCalculator = null,
    portCanalChecks = {},
    isSave = false,
  ) => {
    let updatedCalculatorData = propCalculator
      ? { ...propCalculator }
      : { ...proxyCalculatorData };

    const { getPortDistanceString, routeList } =
      convertPortOperationToDistanceString(updatedCalculatorData);

    const { clientQuery = {} } = updatedCalculatorData;
    const {
      eca = false,
      hra = false,
      jwc = false,
      suez = false,
      panama = false,
      singapore = false,
    } = clientQuery;

    const portDataObj = {
      eca,
      hra,
      jwc,
      suez,
      panama,
      singapore,
      routeOverride: false,
      getGeometry: false,
      ...portCanalChecks,
      routeList,
      portList: getPortDistanceString,
      clientQueryId: updatedCalculatorData.clientQuery?._id,
      queryId: updatedCalculatorData.clientQuery?._id,
      id: analysisId,
    };

    if (portCanalChecks && Object.keys(portCanalChecks).length > 0) {
      onChangePortCanalInQueryState(portCanalChecks);
      updatedCalculatorData = {
        ...updatedCalculatorData,
        clientQuery: {
          ...updatedCalculatorData.clientQuery,
          ...portCanalChecks,
        },
      };
    }

    const request = reset
      ? actions.resetDistance(portDataObj)
      : actions.getPortDistanceByPortList(portDataObj);

    if (!loader)
      setLoader(
        true,
        reset
          ? "Resetting port distances..."
          : "Recalculating port distances...",
      );

    request
      .then((res) => {
        setLoader(false);
        fetchPortDistance.current = false;
        setPortDistanceResponse(res.data);
        const newPortOperations = addPortDistanceToOperations(
          res.data,
          [...updatedCalculatorData.portOperation],
          res?.realData || {},
          false,
          reset,
        );
        sendCalculationRequest({
          ...updatedCalculatorData,
          portOperation: newPortOperations,
          portDistanceResponse: res.data,
          realData: res?.realData || {},
          reset: reset,
        });
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.data?.message || "Failed to calculate distances");
      });
  };

  const onClickCalculate = () => {
    sendCalculationRequest({ ...proxyCalculatorData, autoSave: false });
  };

  const previousExpandedRows = useRef(expandedRows);
  useEffect(() => {
    const newlyOpened = Object.keys(expandedRows).find(
      (key) => expandedRows[key] && !previousExpandedRows.current[key],
    );
    if (newlyOpened) {
      sendCalculationRequest({ ...proxyCalculatorData, autoSave: false });
    }
    previousExpandedRows.current = expandedRows;
  }, [expandedRows]);

  const onChangeSelectedVessel = (e, isFleetEdit = false) => {
    const {
      name = "",
      value = "",
      type = "number",
      checked = false,
    } = e.target || {};

    let newUpdatedFleet = { ...selectedVessels };
    let updatedvessel = { ...JSON.parse(newUpdatedFleet.vessel || "{}") };
    if (isFleetEdit) newUpdatedFleet[name] = value;
    else {
      if (type === "checkbox") updatedvessel[name] = checked;
      else updatedvessel[name] = value;
    }
    const updatedFleet = {
      ...newUpdatedFleet,
      vessel: JSON.stringify(updatedvessel),
    };
    setSelectedVessels(updatedFleet);
    const updatedFleetList = proxyCalculatorData.fleetList.map((item) =>
      item._id === updatedFleet._id ? updatedFleet : item,
    );
    setProxyCalculatorData({
      ...proxyCalculatorData,
      fleetList: updatedFleetList,
    });
  };

  const openSplitHireModal = (fleet) => {
    setSelectedSplitHire(fleet);
    setSplitHireModal(true);
  };

  const onChangeSyncBunker = (checked) => {
    let updatedClientQuery = { ...proxyCalculatorData.clientQuery };
    let clientQuery = { ...updatedClientQuery, fuelPriceOverridden: checked, fuelCalculationMethod: !updatedClientQuery.fuelCalculationMethod || updatedClientQuery.fuelCalculationMethod === "DEFAULT" ? "LIFO" : updatedClientQuery.fuelCalculationMethod };
    if (!checked)
      clientQuery = {
        ...updatedClientQuery,
        fuelPriceOverridden: checked,
        fuelCalculationMethod: "DEFAULT",
      };
    setProxyCalculatorData({ ...proxyCalculatorData, clientQuery });
  };

  const onChangeClientQuery = (e) => {
    const { name, value } = e.target;
    setProxyCalculatorData({
      ...proxyCalculatorData,
      clientQuery: { ...proxyCalculatorData.clientQuery, [name]: value },
    });
  };

  const onChangePortCanalInQueryState = (portCanalChecks = {}) => {
    setProxyCalculatorData((prev) => ({
      ...prev,
      clientQuery: {
        ...prev?.clientQuery,
        ...portCanalChecks,
      },
    }));
  };

  const getCurrentResult = () => { };

  const onChangeChartValue = (calculatorData, isExpanded, vessel) => {
    const id = Object.keys(expandedRows)[0];
    const vesselIndex = calculatorData?.fleetList?.findIndex(
      (i) => i?._id?.toString() == id,
    );
    if (vesselIndex == -1 && !isExpanded) {
      setVoyagesDurationData([]);
      setVoyagesExpensesBreakupData([]);
      return;
    }
    vessel = vessel ? vessel : calculatorData?.fleetList?.[vesselIndex];
    if (
      vessel?.portStayTotal ||
      vessel?.ballastSeaDuration ||
      vessel?.ladenSeaDuration
    )
      setVoyagesDurationData([
        { label: "Port Stay Total", value: vessel?.portStayTotal },
        { label: "Ballast Sea Duration", value: vessel?.ballastSeaDuration },
        { label: "Laden Sea Duration", value: vessel?.ladenSeaDuration },
      ]);
    let totalPortExpenses = 0;
    const len = calculatorData?.portOperation?.length || 0;
    for (let i = 0; i < len; i++) {
      totalPortExpenses += calculatorData?.portOperation[i]?.portExp
        ? Number(calculatorData?.portOperation[i]?.portExp)
        : 0;
    }
    let totalExpenses =
      Number(vessel?.netFrtRevenue || 0) +
      Number(calculatorData?.clientQuery?.miscRevenue || 0) -
      (vessel?.profitNet < 0
        ? -1 * parseInt(vessel?.profitNet || 0)
        : parseInt(vessel?.profitNet || 0));
    if (vessel?.fuelExpense || vessel?.hireExpense || totalPortExpenses)
      setVoyagesExpensesBreakupData([
        { label: "Fuel Expenses", value: vessel?.fuelExpense || 0 },
        { label: "Hire Expenses", value: vessel?.hireExpense || 0 },
        { label: "Total Port Expenses", value: totalPortExpenses || 0 },
        {
          label: "Others Expenses",
          value:
            totalExpenses -
            (vessel?.hireExpense < 0
              ? -1 * parseInt(vessel?.hireExpense || 0)
              : parseInt(vessel?.hireExpense || 0)) -
            (vessel?.fuelExpense < 0
              ? -1 * parseInt(vessel?.fuelExpense || 0)
              : parseInt(vessel?.fuelExpense || 0)) -
            (totalPortExpenses < 0
              ? -1 * parseInt(totalPortExpenses || 0)
              : parseInt(totalPortExpenses || 0)),
        },
      ]);
  };

  return (
    <div className="calculator-section flex flex-col w-full">
      <div className="">
        <ActionButtons
          hideOrder={hideOrder}
          setHideOrder={setHideOrder}
          onSetOpenMap={() => { }}
          onClickCalculate={onClickCalculate}
          onSetHideCalculator={() => setHideCalculator(!hideCalculator)}
          onClickCalculateAndSave={onClickCalculate}
          isFromSingleVesselCalcy={false}
          isPublic
          isFromCargoAnalysis
          pdfdata={{}}
        />
      </div>

      <div className="p-1 w-full">
        <VesselSelection
          disableEdit={true}
          isFromCargoAnalysis
          selectedVessels={selectedVessels}
          onFetchPortOperationDistance={onFetchPortOperationDistance}
          selectedQuery={proxyCalculatorData.clientQuery}
          onChangeFleetRowSelect={onChangeFleetRowSelect}
          onChangeClientQuery={onChangeClientQuery}
          onClickAddFleet={() => setAddFleetModal((p) => !p)}
          addFleetModal={addFleetModal}
          addFleetsArray={addFleetsArray}
          setAddFleetsArray={setAddFleetsArray}
          calculatorData={proxyCalculatorData}
          setCalculatorData={setProxyCalculatorData}
          setLoader={setLoader}
          loader={loader}
          setAddFleetModal={setAddFleetModal}
          sendCalculationRequest={sendCalculationRequest}
          setSelectedVessels={setSelectedVessels}
          splitHireModal={splitHireModal}
          setSplitHireModal={setSplitHireModal}
          selectedSplitHire={selectedSplitHire}
          setSelectedSplitHire={setSelectedSplitHire}
          openSplitHireModal={openSplitHireModal}
          getInitalQueryAndFleetData={() => { }}
          initialFleetList={proxyCalculatorData.fleetList}
          onChangeSelectedVessel={onChangeSelectedVessel}
          fleetCheckbox={fleetCheckbox}
          setFleetCheckbox={setFleetCheckbox}
          setMarginModal={setMarginModal}
          syncBunker={syncBunker}
          setSyncBunker={setSyncBunker}
          setSupplyModal={setSupplyModal}
          onChangeSyncBunker={onChangeSyncBunker}
          isOverride={isOverride}
          calculationErrors={calculationErrors}
          marginModal={marginModal}
          onChangePortOperations={onChangePortOperations}
          onAddOperations={onAddOperations}
          onDeleteOperations={onDeleteOperations}
          supplyModal={supplyModal}
          fetchPortDistance={fetchPortDistance}
          openAddOperation={openAddOperation}
          setOpenAddOperation={setOpenAddOperation}
          setOpenAddOperationindex={setOpenAddOperationindex}
          onChangePortCanalInQueryState={onChangePortCanalInQueryState}
          setIsOverride={setIsOverride}
          hideOrder={hideOrder}
          setHideOrder={setHideOrder}
          hideCalculator={hideCalculator}
          getCurrentResult={getCurrentResult}
          setPdfData={() => { }}
          onChangeChartValue={onChangeChartValue}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
        />
        {(expandedRows && Object.values(expandedRows).some(Boolean)) && (
          <div className="mt-4">
            <div className="flex flex-wrap">
              <div className="w-full md:w-[50%]">
                {voyagesDurationData?.length > 0 && (
                  <PieChart
                    data={voyagesDurationData}
                    label="Voyage Duration Break-up"
                  />
                )}
              </div>
              <div className="w-full md:w-[50%] pr-2">
                {voyagesExpensesBreakupData?.length > 0 && (
                  <PieChart
                    data={voyagesExpensesBreakupData}
                    label="Voyage Expenses Break-up"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VesselAnalysisEstimationTab({ analysis, setLoader, loader = false }) {
  return (
    <div className="vessel-analysis-estimation-tab p-0 bg-[#eff6ff] min-h-screen">
      <SingleAnalysisCopy
        key={`${analysis?._id || "va"}-copy-0`}
        analysis={analysis}
        copyIndex={0}
        setLoader={setLoader}
        loader={loader}
      />
    </div>
  );
}

export default memo(VesselAnalysisEstimationTab);
